import React, { useEffect, useRef } from 'react';
import { DropzoneArea } from 'material-ui-dropzone';
import { Hidden } from '@material-ui/core';
import { useHistory } from 'react-router';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { docUploadStyles } from '../../global/constants/theme/styles/components/doc-upload';
import { DocumentUploadState, GenericDocumentUpload } from '../account-info/features/personal-info/document-upload-models';
import { TcsmStates } from '../../root/tcsm-model';
import { LabelSettingProps, TcsmSiteSettingsProps } from '../../global/models/epi-model';
import { resetDocState, setDocUploadApiStatus, stageDocuments } from '../account-info/features/personal-info/document-upload-slice';
import { DividerSection } from '../../global/components/layout/divider-section';
import { ContentSpacer } from '../../global/components/layout/generic-content-spacer';
import { processRequestWithDocumentUpload } from '../account-info/features/personal-info/document-upload-request';
import { PageContainer } from '../../global/components/layout/page-container';
import { MainContent } from '../../global/components/layout/main-content-container';
import { PageHeader } from '../../global/components/layout/page-header';
import { SideBarContent } from '../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../global/components/guided-process-support-block';
import { GuidedProcessSupportProps } from '../arc/models/arc-epi-props';
import { GlobalErrorMessage } from '../../global/components/global-error-message';
import { apiErrorClear } from '../../global/ducks/base-api';
import { FormSuccessMessage } from '../../global/components/form-element/form-success-message';
import { ApiStatuses } from '../../global/models/api-model';
import { FormTypes } from '../account-info/models/form-models';
import { FormButtons } from '../../global/components/form-element/form-buttons';
import { usePendingRequests } from '../../global/hooks/usePendingRequests';

interface GenericDocumentUploadProps {
	uploadType: GenericDocumentUpload,
}

const source = 'KOFAX';
const nodeType = 'beneform';
export const GenericDocumentUploadPage = (props: GenericDocumentUploadProps) => {
	const { uploadType } = props;
	const classes = docUploadStyles();
	const documentUpoloadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[uploadType]);
	const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state.epiProps?.gpsSettingsState?.epiContent);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);
	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
	const dispatch = useDispatch();
	const setting = siteSettings?.documentUploadSettings?.filter(a => a.formType === props.uploadType)[0];
	const docUploadRef = useRef<HTMLDivElement>(null);
	const guid = useSelector<TcsmStates, string>(state =>
		uploadType === GenericDocumentUpload.MemberDocUpload ?
			state?.authenticatedState?.clientGuid :
			state?.personalInfo?.arc?.currentPerson?.person_guid);
	const pendingRequests = usePendingRequests();
	const history = useHistory();

	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(resetDocState(uploadType));
	}, [uploadType]);
	const pageRef = useRef<any>();
	const gpsTitle = uploadType === GenericDocumentUpload.MemberDocUpload ? gpsSettings?.myCpgDocumentUploadSectionTitle : gpsSettings?.mapDocumentUploadSectionTitle;
	const gpsItem = uploadType === GenericDocumentUpload.MemberDocUpload ? gpsSettings?.myCpgDocumentUploadSectionArea : gpsSettings?.mapDocumentUploadSectionArea;
	const handler = (loadedFiles: File[]) => {
		if (loadedFiles && loadedFiles.length > 0) {
			dispatch(stageDocuments(loadedFiles, uploadType));
		}
	};

	const handlePreviewIcon = (fileObject) => {
		const { name } = fileObject.file;

		return <a href={fileObject?.data} download={name}>{name}</a>;

	};

	const scrollToResponseMessage = () => {
		pageRef?.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const onSuccess = () => {
		dispatch(setDocUploadApiStatus(ApiStatuses.Success, uploadType));
		scrollToResponseMessage();
	};

	const onFailure = () => {
		dispatch(setDocUploadApiStatus(ApiStatuses.Failure, uploadType));
		scrollToResponseMessage();
	};

	const uploadDocument = () => {
		if (documentUpoloadState.stagedDocuments) {
			processRequestWithDocumentUpload(documentUpoloadState, guid, dispatch, onSuccess, onFailure, source, nodeType);
		}
	};

	const onCancel = () => {
		history.push('/temp');
		history.goBack();
	};



	if (pendingRequests) {
		return <LoadingCircle />;
	}

	return (
		<PageContainer >
			<MainContent>
				<div ref={pageRef}>
					<GlobalErrorMessage />
					<FormSuccessMessage types={documentUpoloadState?.uploadStatus === ApiStatuses.Success && [FormTypes.GENERIC_DOC_UPLOAD]} />
				</div>
				<PageHeader
					headerText={siteSettings.genericDocUploadPageTitle}
				/>
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsTitle} contents={gpsItem} />
					</SideBarContent>
				</Hidden>
				<DividerSection topDivider >

					<div className={classes.docUploadContainer} ref={docUploadRef}>
						<ContentSpacer>
							<h2>{setting?.instructionTitle}</h2>
						</ContentSpacer>
						<ContentSpacer>
							<div className='rich-text'>
								<div dangerouslySetInnerHTML={{ __html: setting?.instructionDescription }} />
							</div>
						</ContentSpacer>

						<DropzoneArea
							onChange={handler}
							maxFileSize={Number(setting?.fileSize) || 3000000}
							filesLimit={Number(setting?.maxFileNumber) || 3}
							acceptedFiles={setting?.fileTypes?.split(',') || []}
							getPreviewIcon={handlePreviewIcon}
							previewText={siteSettings?.docUploadChipTitle}
							dropzoneText={setting?.dropzoneText}
							showPreviews
							showPreviewsInDropzone={false}

						/>
					</div>

					<div>
						<FormButtons
							disabled={false}
							submitText={siteSettings?.genericDocUploadText || 'Upload Documents'}
							cancelText={labelSettings?.cancelButtonText || 'Cancel'}
							onSubmit={uploadDocument}
							onCancel={onCancel}
						/>
					</div>
				</DividerSection>

			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsTitle} contents={gpsItem} />
				</SideBarContent>
			</Hidden>
		</PageContainer>



	);

};
